<script setup lang="ts">
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from 'vue'

const { status = null } = defineProps<{ status?: 'error' | 'loading' | 'success' }>()

const emit = defineEmits<{
    change: [string]
    submit: [string]
}>()

const inputRefs = useTemplateRef<HTMLInputElement[]>('input-ref')

const activeInputIndex = ref(0)
const activeInput = computed(() => inputRefs.value?.[activeInputIndex.value])
const isIOS = ref(false)
const state = ref(['', '', '', ''])

function handleFocus() {
    activeInput.value?.focus()
}

function handleInput({ inputType, target }: InputEvent, index: number) {
    if (!(target instanceof HTMLInputElement)) {
        return
    }

    const value = target.value.replace(/\D/g, '')

    if (value.length >= 4) {
        state.value = value.slice(0, 4).split('')
        return
    }

    state.value[index] = value

    if (value && index <= 3) {
        activeInputIndex.value = index + 1

        nextTick(() => {
            inputRefs.value?.[index + 1]?.focus()
            inputRefs.value?.[index + 1]?.setSelectionRange(0, 1)
        })
    }

    if (inputType === 'deleteContentBackward' && index > 0 && !isIOS.value) {
        activeInputIndex.value = index - 1

        nextTick(() => {
            inputRefs.value?.[index - 1]?.focus()
            inputRefs.value?.[index - 1]?.setSelectionRange(0, 1)
        })
    }
}

function handleKeyDown({ key, target }: KeyboardEvent, index: number) {
    if (!(target instanceof HTMLInputElement)) {
        return
    }

    if (key === 'Backspace' && index > 0 && !target.value) {
        nextTick(() => {
            activeInputIndex.value = index - 1
            activeInput.value?.focus()

            if (!isIOS.value) {
                activeInput.value?.setSelectionRange(0, 1)
            }
        })
    }

    if (target.selectionEnd === null) {
        target.setSelectionRange(0, 1)
    }
}

function handlePaste(event: ClipboardEvent) {
    // prettier-ignore
    const data = event.clipboardData
        ?.getData('text')
        .replace(/\D/g, '')
        .slice(0, 4)
        .split('')

    if (data?.length) {
        data.forEach((value, index) => (state.value[index] = value))

        // prettier-ignore
        activeInputIndex.value = data.length < 4
            ? data.length
            : data.length - 1

        nextTick(handleFocus)
    }
}

function reset() {
    activeInputIndex.value = 0
    state.value = ['', '', '', '']
}

watch(
    state,
    (value) => {
        const isValid = value.every((code) => /\d/g.test(code))

        emit('change', value.join(''))

        if (!isValid) return

        emit('submit', value.join(''))
    },
    { deep: true }
)

watch(
    () => status,
    (value) => {
        if (value === 'error') {
            inputRefs.value?.[activeInputIndex.value]?.blur()
        }
    }
)

onMounted(() => {
    handleFocus()
    isIOS.value = /iPhone/gi.test(navigator.userAgent)
})

defineExpose({
    reset,
})
</script>

<template>
    <div class="otp">
        <input
            v-for="(_, index) in state"
            :key="index"
            ref="input-ref"
            v-model="state[index]"
            :class="[
                'otp__input',
                {
                    _error: status === 'error',
                    _loading: status === 'loading',
                    _success: status === 'success',
                },
            ]"
            type="text"
            :readonly="!!status && status !== 'error'"
            autocomplete="one-time-code"
            inputmode="numeric"
            @focus="handleFocus"
            @input="handleInput($event as InputEvent, index)"
            @keydown="handleKeyDown($event, index)"
            @paste.prevent="handlePaste($event)"
        />
    </div>
</template>

<style src="./VOtp.scss"></style>
